import { SceneCommon, SCENE_COMMON_TOKEN } from '@cgs/common';
import { SpinResponse, SpecialSymbolGroup, SpecialSymbolGroupType } from '@cgs/data-access';
import {
  ComponentIndex,
  ComponentNames,
  Entity,
  FlyActionHelper,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IconsSoundModel,
  IconsSoundModelComponent,
  IGameStateMachineProvider,
  IReelsConfig,
  IReelsConfigProvider,
  ISlotGameEngineProvider,
  MultiSceneReelsEngineV2,
  ResourcesComponent,
  T_IconsSoundModelComponent,
  T_IReelsConfigProvider,
  T_ISlotGameEngineProvider,
  RESOURCES_COMPONENT_TOKEN,
} from '@cgs/machines';
import { LazyAction } from '@cgs/shared';
import {
  BitmapTextSceneObject,
  Container,
  easeInOut,
  EmptyAction,
  FunctionAction,
  InterpolateInplaceAction,
  IntervalAction,
  ParallelAction,
  SceneObject,
  SequenceAction,
  Vector2,
} from '@cgs/wge';
import {
  CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN,
  CorsicaFreeSpinsTotalComponent,
} from './corsica-free-spins-total.component';
import { FreeSpinEventType } from './corsica-free-spins.types';
import { PanelStates } from './corsica-icon-with-values/corsica-icon-values.helper';
import { IconValueDescription } from './corsica-icon-with-values/corsica-icon-values.types';

export const CORSICA_FREE_SPINS_UPDATE_SYMBOLS_COMPONENT_TOKEN = Symbol(
  'CorsicaFreeSpinsUpdateSymbolsComponent'
);

export class CorsicaFreeSpinsUpdateSymbolsComponent {
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;
  private readonly reelsEngine: MultiSceneReelsEngineV2;
  private readonly flyActionHelper: FlyActionHelper;
  private readonly gameResourceProvider: ResourcesComponent;
  private readonly reelsConfig: IReelsConfig;
  private readonly totalComponent: CorsicaFreeSpinsTotalComponent;

  private readonly iconSoundModel: IconsSoundModel;

  private readonly iconValueComponentIndex: ComponentIndex;
  private readonly iconNodeComponentIndex: ComponentIndex;
  private readonly positionComponentIndex: ComponentIndex;
  private readonly drawableComponentIndex: ComponentIndex;

  private readonly reelSize: number;

  constructor(container: Container) {
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.reelsEngine = container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider)
      .reelsEngine as MultiSceneReelsEngineV2;

    this.gameResourceProvider = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);

    this.totalComponent = container.resolve<CorsicaFreeSpinsTotalComponent>(
      CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN
    );

    this.iconValueComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.IconValue
    );

    this.iconNodeComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.IconNode
    );

    this.positionComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.Position
    );

    this.drawableComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.DrawableIndex
    );

    this.reelsConfig = container.resolve<IReelsConfigProvider>(T_IReelsConfigProvider).reelsConfig;
    this.reelSize = this.reelsConfig.lineCount * this.reelsConfig.reelCount;

    const sceneCommon = container.resolve<SceneCommon>(SCENE_COMMON_TOKEN)!;
    this.flyActionHelper = new FlyActionHelper(container, sceneCommon);

    this.iconSoundModel = container.resolve<IconsSoundModelComponent>(
      T_IconsSoundModelComponent
    ).iconsSoundModel;

    this.gameStateMachine.animation.appendLazyAnimation(() => this.updateSymbols());
    this.gameStateMachine.stop.appendLazyAnimation(() => this.updateStoppingSymbols());
  }

  public flyAnimation(
    flyingNode: SceneObject,
    startPos: Vector2,
    endPos: Vector2,
    duration: number = 0.5
  ): IntervalAction {
    const lazyAction = new LazyAction(() => {
      const interpolateAction = new InterpolateInplaceAction<Vector2>((v) => v.clone())
        .withInterpolateFunction(Vector2.lerpInplace)
        .withValues(startPos, endPos)
        .withTimeFunction(easeInOut)
        .withDuration(duration);

      interpolateAction.valueChange.listen((e) => {
        flyingNode.position = e;
      });

      return interpolateAction;
    });

    return new SequenceAction([lazyAction, new EmptyAction().withDuration(duration)]);
  }

  private flyTo(
    startEntity: Entity,
    targetEntity: Entity,
    sceneName: string,
    duration: number
  ): IntervalAction {
    const flyingScene = this.flyActionHelper.loadSceneToSlot(sceneName, true);

    const animIconsHolder =
      this.gameResourceProvider.slot.findById<SceneObject>('anim_icons_holder')!;

    const multiplierText = flyingScene.findById<BitmapTextSceneObject>('mult_collect')!;
    multiplierText.position = multiplierText.size.clone().mulNum(-0.5);

    const startPos = this.getMovingScenePosition(startEntity);
    const endPos = this.getMovingScenePosition(targetEntity);

    return new SequenceAction([
      new FunctionAction(() => {
        const multiplier = startEntity.get<IconValueDescription>(this.iconValueComponentIndex);
        multiplierText.text = multiplier.value.toString();
        this.flyActionHelper.addCreatedSceneToSlot(flyingScene, startPos, animIconsHolder);
      }),
      new ParallelAction([
        this.flyAnimation(flyingScene, startPos, endPos, duration),
        new FunctionAction(() => {
          flyingScene?.stateMachine?.switchToState('anim');
        }),
      ]),
      this.flyActionHelper.removeCreatedSceneFromSlotAction(flyingScene) as IntervalAction,
    ]);
  }

  private getMovingScenePosition(entity: Entity): Vector2 {
    const entityPosition = entity.get<Vector2>(this.positionComponentIndex);

    const iconNode = entity.get<SceneObject>(this.iconNodeComponentIndex);

    const collectPoint = iconNode.findById<SceneObject>('collect_point')!;

    const symbolSize = this.reelsConfig.symbolSize;

    return entityPosition
      .subtract(symbolSize.clone().mulNum(-0.5))
      .add(collectPoint.position.clone());
  }

  private updateSymbols(): IntervalAction {
    if (!this.gameStateMachine.curResponse.isFreeSpins) {
      return new EmptyAction();
    }

    const animations: IntervalAction[] = [new EmptyAction()];

    const specGroups = this.gameStateMachine.curResponse.specialSymbolGroups || [];

    specGroups
      .filter(
        (group) =>
          group.type === SpecialSymbolGroupType.Multiplier &&
          group.subType !== FreeSpinEventType.Init
      )
      .forEach((group) => {
        if (group.subType === FreeSpinEventType.Collect) {
          this.updateCollector(group, animations);
          return;
        }

        if (group.subType === FreeSpinEventType.Add) {
          this.updateDistributor(group, animations);
          return;
        }
      });

    return new SequenceAction(animations);
  }

  private updateStoppingSymbols(): IntervalAction {
    if (!this.gameStateMachine.curResponse.isFreeSpins) {
      return new EmptyAction();
    }

    return new FunctionAction(() => {
      (this.gameStateMachine.curResponse.specialSymbolGroups || [])
        .filter(
          (group) =>
            group.type === SpecialSymbolGroupType.Multiplier &&
            group.subType === FreeSpinEventType.Init
        )
        .forEach((group) => {
          if (!group.positions?.length) {
            return;
          }

          const reel = this.reelsEngine.getReelByPosition(group.positions[0]);
          const line = this.reelsEngine.getLineByPosition(group.positions[0]);

          const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

          if (!entity) {
            return;
          }

          const valueDescription = entity.get<IconValueDescription>(this.iconValueComponentIndex);

          if (valueDescription) {
            valueDescription.value = group.totalWin!;
            valueDescription.panelAnimState = PanelStates.Static;
            entity.addComponent(ComponentNames.IconValue, valueDescription);
            entity.addComponent(ComponentNames.NeedUpdate, true);
          }
        });
    });
  }

  private updateCollector(group: SpecialSymbolGroup, animations: IntervalAction[]) {
    const symbolPosition = group.positions![0];
    const winPositions = group.positionsWin || [];

    const collectorReel = this.reelsEngine.getReelByPosition(symbolPosition);
    const collectorLine = this.reelsEngine.getLineByPosition(symbolPosition);
    const collectorEntity = this.reelsEngine.getStoppedEntities(collectorReel, collectorLine)[0];

    if (!collectorEntity) {
      return;
    }

    const collectFromSymbols = new Map<number, { entity: Entity }>();

    winPositions.forEach((position) => {
      const reel = this.reelsEngine.getReelByPosition(position);
      const line = this.reelsEngine.getLineByPosition(position);
      const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

      if (!entity) {
        return;
      }

      collectFromSymbols.set(position, { entity });
    });

    const entries = Array.from(collectFromSymbols.entries()).sort(
      ([positionA], [positionB]) => positionA - positionB
    );
    const iconNode = collectorEntity.get<SceneObject>(this.iconNodeComponentIndex);

    const drawableIndex = collectorEntity.get<number>(this.drawableComponentIndex);
    const collectSound = this.iconSoundModel.getIconSound(drawableIndex);

    animations.push(
      ...[
        new FunctionAction(() => {
          this.reelsEngine.startAnimation(collectorEntity, 'start');
          collectSound.GoToState('stop');
          collectSound.GoToState('enter');
        }),
        new EmptyAction().withDuration(iconNode.stateMachine?.getStateDuration('start') || 0),
        new FunctionAction(() => {
          this.reelsEngine.startAnimation(collectorEntity, 'middle');
        }),
      ]
    );

    const parallelActions: IntervalAction[] = [];

    const flyDuration = 0.3;
    const offsetDuration = flyDuration * 0.5;

    entries.forEach(([_, collectFromSymbol], index) => {
      const subActions: IntervalAction[] = [];

      subActions.push(
        new FunctionAction(() => {
          collectSound.GoToState('stop');
        })
      );

      subActions.push(new EmptyAction().withDuration(index * offsetDuration));

      subActions.push(
        new FunctionAction(() => {
          collectSound.GoToState('play');
        })
      );

      subActions.push(
        this.flyTo(collectFromSymbol.entity, collectorEntity, 'slot/collectable', flyDuration)
      );

      subActions.push(
        new FunctionAction(() => {
          collectSound.GoToState('stop');
        })
      );

      subActions.push(
        new FunctionAction(() => {
          const collectFromValueDescription = collectFromSymbol.entity.get<IconValueDescription>(
            this.iconValueComponentIndex
          );

          const collectorValueDescription = collectorEntity.get<IconValueDescription>(
            this.iconValueComponentIndex
          );

          collectorValueDescription.panelAnimState = PanelStates.Anim;
          collectorValueDescription.value += collectFromValueDescription.value;

          collectorEntity.addComponent(ComponentNames.IconValue, collectorValueDescription);
          collectorEntity.addComponent(ComponentNames.NeedUpdate, true);

          this.totalComponent.addToTotal(collectFromValueDescription.value);
        })
      );

      parallelActions.push(new SequenceAction(subActions));
    });

    animations.push(new ParallelAction(parallelActions));

    animations.push(
      ...[
        new FunctionAction(() => {
          collectSound.GoToState('stop');
          collectSound.GoToState('finish');
          this.reelsEngine.startAnimation(collectorEntity, 'end');
        }),
        new EmptyAction().withDuration(iconNode.stateMachine?.getStateDuration('end') || 0),
        new FunctionAction(() => {
          this.reelsEngine.stopAnimation(collectorEntity, 'end');
        }),
      ]
    );
  }

  private updateDistributor(group: SpecialSymbolGroup, animations: IntervalAction[]) {
    const winPositions = group.positionsWin || [];
    const symbolPosition = group.positions![0];

    const distributorReel = this.reelsEngine.getReelByPosition(symbolPosition);
    const distributorLine = this.reelsEngine.getLineByPosition(symbolPosition);
    const distributorEntity = this.reelsEngine.getStoppedEntities(
      distributorReel,
      distributorLine
    )[0];

    if (!distributorEntity) {
      return;
    }

    const targetsByReel = new Map<number, { entity: Entity; position: number }[]>();

    winPositions.forEach((position) => {
      const reel = this.reelsEngine.getReelByPosition(position);
      const line = this.reelsEngine.getLineByPosition(position);
      const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

      if (!entity) {
        return;
      }

      if (!targetsByReel.has(reel)) {
        targetsByReel.set(reel, []);
      }

      targetsByReel.get(reel)!.push({ entity, position: position });
    });

    const iconNode = distributorEntity.get<SceneObject>(this.iconNodeComponentIndex);

    const drawableIndex = distributorEntity.get<number>(this.drawableComponentIndex);

    const distributorSound = this.iconSoundModel.getIconSound(drawableIndex);

    animations.push(
      ...[
        new FunctionAction(() => {
          distributorSound.GoToState('stop');
          distributorSound.GoToState('enter');
          this.reelsEngine.startAnimation(distributorEntity, 'start');
          iconNode.z = iconNode.z + this.reelSize;
        }),
        new EmptyAction().withDuration(iconNode.stateMachine?.getStateDuration('start') || 0),
        new FunctionAction(() => {
          this.reelsEngine.startAnimation(distributorEntity, 'middle');
        }),
      ]
    );

    const entries = Array.from(targetsByReel.entries()).sort(
      ([positionA], [positionB]) => positionA - positionB
    );

    const flyDuration = 0.3;

    const parallelActions: IntervalAction[] = [];

    entries.forEach(([reel, targets]) => {
      const subActions: IntervalAction[] = [];
      const stateName = reel >= distributorReel ? 'right' : 'left';

      subActions.push(
        new FunctionAction(() => {
          distributorSound.GoToState('stop');
        }),
        new FunctionAction(() => {
          this.reelsEngine.startAnimation(distributorEntity, stateName);
        }),
        new EmptyAction().withDuration(
          (iconNode.stateMachine?.getStateDuration(stateName) || 0) * 0.2
        ),

        new FunctionAction(() => {
          distributorSound.GoToState('play');
        })
      );

      const reelActions: IntervalAction[] = [];

      targets
        .sort((a, b) => b.position - a.position)
        .forEach((target) => {
          const actions: IntervalAction[] = [];
          actions.push(
            this.flyTo(
              distributorEntity,
              target.entity,
              'slot/collectable',
              iconNode.stateMachine?.getStateDuration(stateName) || flyDuration
            )
          );

          actions.push(
            new FunctionAction(() => {
              const targetValueDescription = target.entity.get<IconValueDescription>(
                this.iconValueComponentIndex
              );

              const distributorValueDescription = distributorEntity.get<IconValueDescription>(
                this.iconValueComponentIndex
              );

              targetValueDescription.panelAnimState = PanelStates.Anim;
              targetValueDescription.value += distributorValueDescription.value;

              target.entity.addComponent(ComponentNames.IconValue, targetValueDescription);
              target.entity.addComponent(ComponentNames.NeedUpdate, true);

              this.totalComponent.addToTotal(distributorValueDescription.value);
            })
          );
          reelActions.push(new SequenceAction(actions));
        });

      subActions.push(new ParallelAction(reelActions));

      subActions.push(
        new FunctionAction(() => {
          this.reelsEngine.startAnimation(distributorEntity, 'middle');
        })
      );

      subActions.push(new EmptyAction().withDuration(0.01));

      parallelActions.push(new SequenceAction(subActions));
    });

    animations.push(new SequenceAction(parallelActions));

    animations.push(
      ...[
        new FunctionAction(() => {
          distributorSound.GoToState('stop');
          distributorSound.GoToState('finish');
          this.reelsEngine.startAnimation(distributorEntity, 'end');
          iconNode.z = iconNode.z - this.reelSize;
        }),
        new EmptyAction().withDuration(iconNode.stateMachine?.getStateDuration('end') || 0),
        new FunctionAction(() => {
          this.reelsEngine.stopAnimation(distributorEntity, 'end');
        }),
      ]
    );
  }
}
